import {
  CalendarDays, Route, Users, PhoneCall, MessageSquare, ShieldCheck, FileText, Receipt,
  CreditCard, BarChart3, Smartphone, Zap, ClipboardList, LayoutDashboard, Package,
  Clock, Bell, MapPin, Search, Repeat, Lock, PenTool, DollarSign, TrendingUp, Camera,
  Star, Globe, Layers, Filter, Wallet,
} from "lucide-react";

export const FEATURES = [
  {
    slug: "job-scheduling",
    name: "Job Scheduling",
    icon: CalendarDays,
    seoTitle: "Job Scheduling Software for Field Service | Fieldz AI",
    seoDescription: "Drag-and-drop job scheduling software for field service teams. Calendar, list, and timeline views, technician availability, and automatic SMS confirmations.",
    h1: "Job scheduling software built for field service teams",
    intro: "See every technician's day at a glance, book jobs in seconds with drag-and-drop, and keep customers informed with automatic confirmations — all from one calendar.",
    img: "/marketing/fieldz-ai-job-scheduling-calendar.webp",
    imgAlt: "Fieldz AI job scheduling calendar with drag-and-drop booking",
    benefits: [
      { icon: CalendarDays, title: "Three views, one truth", body: "Switch between calendar, list, and timeline views — dispatchers, owners, and office staff all work from the same live schedule." },
      { icon: Clock, title: "Book in seconds", body: "Create a job with customer, address, time, and technician in one dialog. Drag to reschedule — the system handles the rest." },
      { icon: Bell, title: "Automatic confirmations", body: "Customers get SMS confirmations and reminders the moment a job is booked or moved, cutting no-shows without extra work." },
      { icon: Filter, title: "Status at a glance", body: "Color-coded statuses track every job from estimate to scheduled, in-progress, completed, and paid." },
    ],
    bullets: [
      "Drag-and-drop rescheduling on day and week views",
      "Technician filter to see one person's route instantly",
      "Recurring jobs for maintenance contracts",
      "Job tickets with all details your techs need on site",
      "Walk-in and phone bookings in the same flow",
      "Completed jobs auto-clear from the live timeline",
    ],
    faqs: [
      { q: "Can I see schedules per technician?", a: "Yes — filter the calendar by technician or use the timeline view to see every tech's day side by side, making gaps and conflicts obvious." },
      { q: "Do customers get notified when I book or move a job?", a: "Yes. Automatic SMS confirmations and reminders go out when jobs are booked or rescheduled. Templates are fully editable." },
      { q: "Does scheduling work on mobile?", a: "Yes — the schedule works in any mobile browser and in the Fieldz AI mobile app, and technicians see their own assignments in the technician portal." },
    ],
    related: ["dispatch", "technician-app", "automations"],
  },
  {
    slug: "dispatch",
    name: "Dispatch",
    icon: Route,
    seoTitle: "Field Service Dispatch Software | Fieldz AI",
    seoDescription: "Dispatch software for field service: assign the right technician, notify them by SMS and push, and track job progress in real time across your team.",
    h1: "Dispatch software that gets the right tech to the right job",
    intro: "Assign jobs with full visibility into who's available, notify technicians instantly by SMS and push, and watch statuses update in real time as work happens.",
    img: "/marketing/fieldz-ai-dispatch-timeline.webp",
    imgAlt: "Fieldz AI dispatch timeline showing technician assignments",
    benefits: [
      { icon: Route, title: "Timeline dispatching", body: "The timeline view lays out every technician's jobs on one screen so you can slot new work into real gaps, not guesses." },
      { icon: Bell, title: "Instant tech notifications", body: "Assigned technicians get an SMS and mobile push with the job ticket — address, time, notes, and contact details." },
      { icon: MapPin, title: "Location-aware", body: "Jobs carry full structured addresses with one-tap Google or Apple Maps navigation for your techs." },
      { icon: Repeat, title: "Live status flow", body: "Techs update status from the field — you see accepted, en-route, started, and completed in real time." },
    ],
    bullets: [
      "Assign one or multiple technicians per job",
      "Job approval flow — techs accept or reject with reasons",
      "Editable assignment SMS templates with smart placeholders",
      "Unassigned-job alerts on your dashboard",
      "Reassign in one click when plans change",
      "Multi-location routing for branch-based teams",
    ],
    faqs: [
      { q: "How do technicians receive dispatched jobs?", a: "They get an SMS with a job ticket link plus a push notification in the mobile app. The ticket shows time, address, customer contact, and job notes." },
      { q: "Can a technician reject a job?", a: "Yes — the approval flow lets techs accept or decline with a reason, so dispatchers can reassign quickly instead of finding out on the day." },
      { q: "Can I dispatch across multiple locations?", a: "Yes. Multi-location plans route jobs to the right branch, and branch managers can dispatch within their own location." },
    ],
    related: ["job-scheduling", "technician-app", "business-phone"],
  },
  {
    slug: "crm",
    name: "Customer CRM",
    icon: Users,
    seoTitle: "Field Service CRM Software | Fieldz AI",
    seoDescription: "A CRM built for field service: complete customer history, every call and text linked automatically, multiple service addresses, and powerful global search.",
    h1: "A CRM that remembers every customer, job, and conversation",
    intro: "Every job, invoice, phone call, and text message is linked to the customer automatically — so anyone on your team has the full story in one click.",
    img: "/marketing/fieldz-ai-customer-crm.webp",
    imgAlt: "Fieldz AI customer CRM database",
    overlay: "/marketing/fieldz-ai-customer-profile.webp",
    overlayAlt: "Fieldz AI customer profile with full service history",
    benefits: [
      { icon: Users, title: "One profile, full history", body: "Jobs, estimates, invoices, payments, calls, and texts — everything about a customer lives on one timeline." },
      { icon: PhoneCall, title: "Calls matched automatically", body: "When a customer calls your business line, Fieldz AI matches the number and pops their profile with previous jobs." },
      { icon: MapPin, title: "Multiple addresses", body: "Track home, rental, and business properties under one customer with per-address job history." },
      { icon: Search, title: "Find anyone in seconds", body: "Global search matches names, phone numbers, addresses, and ZIP codes across customers, jobs, and invoices." },
    ],
    bullets: [
      "Automatic customer creation from inbound calls and lead forms",
      "Notes and attachments shared across your team",
      "Customer source tracking for marketing attribution",
      "Click any past job to open it directly from the profile",
      "Duplicate-safe phone-number matching",
      "Works hand-in-hand with the customer portal",
    ],
    faqs: [
      { q: "Can I import my existing customer list?", a: "Yes — contact our team after signing up and we'll help migrate your customers, jobs, and history into Fieldz AI." },
      { q: "Does the CRM link phone calls to customers?", a: "Yes. Calls to your Fieldz AI business numbers are matched by phone number, logged on the customer's profile, and shown with recordings and durations." },
      { q: "Can customers have more than one service address?", a: "Yes — add as many addresses as needed per customer, and each job records exactly which property was serviced." },
    ],
    related: ["business-phone", "customer-portal", "lead-capture"],
  },
  {
    slug: "business-phone",
    name: "Business Phone",
    icon: PhoneCall,
    seoTitle: "Business Phone System for Field Service | Fieldz AI",
    seoDescription: "A built-in business phone system: dedicated numbers, in-app dialer, call recording, voicemail, smart routing, and calls matched to customers automatically.",
    h1: "A business phone system built into your field service software",
    intro: "Get dedicated business numbers, take calls in your browser or mobile app, and see exactly who's calling — with their job history — before you pick up.",
    img: "/marketing/fieldz-ai-call-history.webp",
    imgAlt: "Fieldz AI business phone call history",
    benefits: [
      { icon: PhoneCall, title: "No hardware needed", body: "Provision local numbers inside Fieldz AI and start calling from your browser or the mobile app the same day." },
      { icon: Users, title: "Caller ID with context", body: "Incoming calls pop the matched customer with previous jobs and calls, so you answer prepared." },
      { icon: Route, title: "Smart call routing", body: "Ring everyone, sequential, or round-robin — with business-hours rules and after-hours voicemail or forwarding." },
      { icon: Layers, title: "Recordings & voicemail", body: "Call recordings, voicemails, and missed-call logs all live in one searchable call history." },
    ],
    bullets: [
      "Dedicated local numbers for US and Canada",
      "In-app dialer with hold, resume, and caller-ID selection",
      "Per-number call flows: team ringing, forwarding, voicemail",
      "Business-hours routing per line and location",
      "Missed-call tracking with dashboard alerts",
      "Usage minutes included with every plan",
    ],
    faqs: [
      { q: "Do I need to buy phones or hardware?", a: "No. Numbers are provisioned in the app and calls work from your computer's browser or the Fieldz AI mobile app — your team keeps their existing devices." },
      { q: "Can multiple people answer the same line?", a: "Yes — choose ring-everyone, sequential, or round-robin routing per number, and pick exactly which team members ring." },
      { q: "Are calls recorded?", a: "Call recording is available on your business lines, and recordings are stored alongside the call log and the customer's profile." },
    ],
    related: ["sms-messaging", "call-masking", "crm"],
  },
  {
    slug: "sms-messaging",
    name: "SMS Messaging",
    icon: MessageSquare,
    seoTitle: "Business SMS & Text Messaging for Field Service | Fieldz AI",
    seoDescription: "Two-way business texting from your field service software: SMS conversations tied to customers, payment links by text, templates, and automated reminders.",
    h1: "Text customers from the number they already know",
    intro: "Two-way SMS conversations live next to your jobs and calls — send confirmations, updates, payment links, and review requests without switching apps.",
    img: "/marketing/fieldz-ai-sms-messaging.webp",
    imgAlt: "Fieldz AI two-way SMS messaging inbox",
    benefits: [
      { icon: MessageSquare, title: "A real messaging inbox", body: "Threaded conversations per customer with search, unread filters, and archived chats — like a messaging app for your business line." },
      { icon: FileText, title: "Templates with placeholders", body: "Reusable SMS templates auto-fill customer names, job times, addresses, and technician details." },
      { icon: CreditCard, title: "Payment links by text", body: "Text a secure Stripe payment link for any invoice and watch the payment record itself when the customer pays." },
      { icon: Zap, title: "Automated messages", body: "Appointment reminders, follow-ups, and review requests send themselves based on job status." },
    ],
    bullets: [
      "Two-way texting on your dedicated business numbers",
      "Conversation search across full message history",
      "Job tickets and estimates sent as SMS links",
      "Unread and archived filters to keep the inbox clean",
      "Automatic customer matching by phone number",
      "SMS usage included with every plan",
    ],
    faqs: [
      { q: "Can customers text me back?", a: "Yes — it's full two-way SMS. Replies land in your Messages inbox, tied to the customer's profile, with notifications for unread messages." },
      { q: "Can I text a payment link?", a: "Yes. From any invoice you can create a secure Stripe checkout link and text it directly. When the customer pays, the invoice updates automatically." },
      { q: "Are reminders sent automatically?", a: "Yes — automations can send booking confirmations, day-before reminders, en-route notices, and post-job review requests without manual effort." },
    ],
    related: ["business-phone", "automations", "invoicing"],
  },
  {
    slug: "call-masking",
    name: "Call Masking",
    icon: ShieldCheck,
    seoTitle: "Call Masking for Field Service Technicians | Fieldz AI",
    seoDescription: "Protect technician privacy and your customer relationships: techs call customers through your business number with PIN-secured call masking.",
    h1: "Call masking that protects your techs — and your business",
    intro: "Technicians call customers through your business number, never their personal cell. Customers see your brand, techs keep their privacy, and you keep the relationship.",
    img: "/marketing/fieldz-ai-call-history.webp",
    imgAlt: "Fieldz AI call history with masked technician calls",
    benefits: [
      { icon: ShieldCheck, title: "Personal numbers stay private", body: "Techs dial a masking number and enter a job PIN — the system bridges the call so their cell number is never exposed." },
      { icon: Lock, title: "Your customers stay yours", body: "All customer contact runs through numbers you own, so relationships don't walk out the door with a technician." },
      { icon: PhoneCall, title: "Branded caller ID", body: "Customers see your business number on every tech call, keeping communication consistent and professional." },
      { icon: Layers, title: "Every call logged", body: "Masked calls are logged against the job automatically, giving you a complete communication trail." },
    ],
    bullets: [
      "Per-technician masking numbers or one shared line",
      "PIN-secured dialing tied to each assigned job",
      "Primary and secondary customer number selection",
      "Masking-aware SMS placeholders in tech notifications",
      "Real numbers never leak into tech-facing messages",
      "Works from any phone — no app required to dial",
    ],
    faqs: [
      { q: "How does a technician call a customer with masking on?", a: "The tech dials their assigned masking number, enters the job PIN, and chooses the customer's primary or secondary number. The system bridges the call showing your business caller ID." },
      { q: "Can each technician have their own masking number?", a: "Yes — assign a dedicated number per technician, or use one account-level masking number shared by the team." },
      { q: "What does the customer see?", a: "Your business number. The technician's personal cell is never shown, and callbacks come to your business line — not the tech's phone." },
    ],
    related: ["business-phone", "technician-app", "dispatch"],
  },
  {
    slug: "estimates",
    name: "Estimates",
    icon: PenTool,
    seoTitle: "Estimate Software for Field Service | Fieldz AI",
    seoDescription: "Create professional estimates in minutes, send by text or email, collect e-signature approvals, and convert to invoices in one click.",
    h1: "Estimates your customers can approve from their phone",
    intro: "Build professional, branded estimates in minutes, send them by text or email, and collect legally-binding e-signatures — then convert to an invoice in one click.",
    img: "/marketing/fieldz-ai-invoice-software.webp",
    imgAlt: "Fieldz AI estimate and invoice builder",
    benefits: [
      { icon: PenTool, title: "Fast, professional quotes", body: "Line items from your saved catalog, per-item and whole-document discounts, taxes, and notes — assembled in minutes." },
      { icon: FileText, title: "E-signature approval", body: "Customers review and sign on any device. Signed documents lock automatically for a clean audit trail." },
      { icon: Repeat, title: "One-click conversion", body: "Approved estimate becomes an invoice instantly — items, discounts, and taxes carry over exactly." },
      { icon: Smartphone, title: "Estimates from the field", body: "Technicians build and send estimates on-site from the mobile portal, with your saved item library." },
    ],
    bullets: [
      "Saved item library with prices, SKUs, and categories",
      "Percentage or flat discounts, per-line or per-document",
      "Automatic tax calculation by service address",
      "Public estimate links — no login needed to approve",
      "PDF export with your logo and branding",
      "Deposit requests on approved estimates",
    ],
    faqs: [
      { q: "How do customers approve an estimate?", a: "They open a secure link (sent by SMS or email), review line items, and sign directly on their phone or computer. You're notified the moment they approve." },
      { q: "Can technicians create estimates on-site?", a: "Yes — the technician portal includes the estimate builder with your saved item library, so quotes go out before the tech leaves the driveway." },
      { q: "What happens after approval?", a: "Convert the estimate to an invoice in one click. You can also collect a deposit right away using a secure payment link." },
    ],
    related: ["invoicing", "payments", "technician-app"],
  },
  {
    slug: "invoicing",
    name: "Invoicing",
    icon: Receipt,
    seoTitle: "Invoicing Software for Field Service Businesses | Fieldz AI",
    seoDescription: "Professional invoicing for field service: branded invoices, online payments, deposits and partial payments, automatic receipts, and real-time status tracking.",
    h1: "Invoicing software that gets you paid without chasing",
    intro: "Send branded invoices by text or email with a secure pay button, accept deposits and partial payments, and watch statuses update in real time as money comes in.",
    img: "/marketing/fieldz-ai-invoice-software.webp",
    imgAlt: "Fieldz AI invoicing software with payment tracking",
    benefits: [
      { icon: Receipt, title: "Branded, professional invoices", body: "Your logo, your terms, clean line items with discounts and tax — delivered as a link or PDF." },
      { icon: CreditCard, title: "Built-in pay button", body: "Every invoice can carry a secure Stripe checkout link, so paying takes one tap instead of a phone call." },
      { icon: DollarSign, title: "Deposits & partial payments", body: "Collect deposits up front and track partial payments with a full history on every invoice." },
      { icon: Bell, title: "Live payment status", body: "Draft, sent, partially paid, paid, overdue — statuses update automatically, with an outstanding-balance dashboard." },
    ],
    bullets: [
      "One-click estimate-to-invoice conversion",
      "Automatic receipts when payments record",
      "Recurring invoice schedules for contracts",
      "Overdue tracking with dashboard alerts",
      "Payment history with card, cash, and check records",
      "CSV and PDF export for your accountant",
    ],
    faqs: [
      { q: "How do customers pay an invoice?", a: "Through a secure Stripe checkout link sent by text or email, on the customer portal, or in person with your technician. Cash and check payments can be recorded manually too." },
      { q: "Can I take a deposit before the job?", a: "Yes — request a deposit on any estimate or invoice. The remaining balance tracks automatically until it's fully paid." },
      { q: "Do invoices sync with my jobs?", a: "Yes. Invoices link to jobs and customers, payments update job financials, and revenue flows straight into your financial reports." },
    ],
    related: ["payments", "estimates", "financial-reporting"],
  },
  {
    slug: "payments",
    name: "Payments",
    icon: CreditCard,
    seoTitle: "Payment Processing for Field Service | Fieldz AI",
    seoDescription: "Take card payments online or in the field with Stripe-powered processing: text-to-pay links, technician collection, deposits, tips, and automatic reconciliation.",
    h1: "Take payments anywhere — online, by text, or in the field",
    intro: "Stripe-powered payments built into every workflow: text a pay link, charge a card on-site from the technician app, or let customers pay from their portal.",
    img: "/marketing/fieldz-ai-invoice-software.webp",
    imgAlt: "Fieldz AI payments on invoices",
    benefits: [
      { icon: CreditCard, title: "Text-to-pay links", body: "Create a secure checkout link for any amount — full balance, deposit, or partial — and send it by SMS or email." },
      { icon: Smartphone, title: "Techs collect on-site", body: "Technicians take card payments from the field through the mobile portal — money lands in your account, not theirs." },
      { icon: ShieldCheck, title: "PCI-compliant by design", body: "Card data is handled entirely by Stripe. Fieldz AI never stores card numbers." },
      { icon: Repeat, title: "Self-reconciling records", body: "When a customer pays, the invoice, job, and financial reports all update automatically — including receipts." },
    ],
    bullets: [
      "Stripe onboarding in minutes with payouts to your bank",
      "Deposits, partial payments, and tips supported",
      "Payments feed with fees, sources, and statuses",
      "Automatic receipt emails on every payment",
      "Guest pay page — customers pay without an account",
      "Refund handling with full audit trail",
    ],
    faqs: [
      { q: "How fast do I get my money?", a: "Payments settle through your own Stripe account with standard Stripe payout timing to your bank — typically two business days in the US." },
      { q: "Can my technicians collect payments?", a: "Yes — techs charge cards from the mobile portal with the amount pre-filled from the invoice. Funds route to your business account automatically." },
      { q: "What are the processing costs?", a: "You pay Stripe's standard card processing plus a small platform fee shown transparently on each transaction — no hidden monthly gateway charges." },
    ],
    related: ["invoicing", "technician-app", "financial-reporting"],
  },
  {
    slug: "financial-reporting",
    name: "Financial Reporting",
    icon: BarChart3,
    seoTitle: "Financial Reporting & Job Costing Software | Fieldz AI",
    seoDescription: "Job-level profitability for field service: revenue, parts, technician commissions, processing fees, and tax — reported per job, per tech, and per location.",
    h1: "Know exactly what every job — and every tech — earns you",
    intro: "Real job costing without spreadsheets: revenue, parts, commissions, fees, and tax broken down per job, per technician, per lead source, and per location.",
    img: "/marketing/fieldz-ai-financial-reports.webp",
    imgAlt: "Fieldz AI financial reports with job profitability",
    benefits: [
      { icon: BarChart3, title: "True job profitability", body: "Every job shows revenue minus parts, commissions, and processing fees — so you know your real margin, not a guess." },
      { icon: Users, title: "Commission settlements", body: "Per-technician commission tracking with one-click settlements and a clear owed-vs-settled ledger." },
      { icon: DollarSign, title: "Tax handled correctly", body: "Tax collected is tracked separately and never inflates revenue — your reports match what your accountant expects." },
      { icon: TrendingUp, title: "Trends you can act on", body: "Compare periods, track lead-source performance, and watch operating results move on your dashboard." },
    ],
    bullets: [
      "Technician reports with commission splits and settlements",
      "Job-source reports for marketing ROI",
      "Expense tracking with categories and trends",
      "Outstanding balance and deposit visibility",
      "Custom date ranges with timezone-correct boundaries",
      "CSV and PDF exports of every report",
    ],
    faqs: [
      { q: "Can I track technician commissions?", a: "Yes — set commission rules per technician, see what's owed in real time, and settle balances with one click. Settled amounts drop to zero automatically." },
      { q: "Does reporting handle sales tax correctly?", a: "Yes. Tax is computed per service address, tracked separately from revenue, and reported in its own tax summary — so revenue is never overstated." },
      { q: "Can I see which lead sources make money?", a: "Yes — job-source reports break revenue and profit down by where the job came from, so you know which marketing channels actually pay." },
    ],
    related: ["invoicing", "payments", "job-scheduling"],
  },
  {
    slug: "technician-app",
    name: "Technician App",
    icon: Smartphone,
    seoTitle: "Technician Mobile App for Field Service | Fieldz AI",
    seoDescription: "A mobile portal for field technicians: assigned jobs, navigation, status updates, photos, on-site estimates, payments, and earnings visibility.",
    h1: "Your technicians' entire day, in their pocket",
    intro: "Techs see their jobs, navigate to the address, update statuses, attach photos, build estimates, and collect payments — everything syncs to the office instantly.",
    phoneImgs: ["/marketing/fieldz-ai-technician-app-dashboard.webp", "/marketing/fieldz-ai-technician-portal.webp"],
    imgAlt: "Fieldz AI technician mobile app",
    benefits: [
      { icon: Smartphone, title: "Everything on one screen", body: "Today's jobs, upcoming work, and alerts — techs open the app and know exactly where to be." },
      { icon: MapPin, title: "One-tap navigation", body: "Full service addresses with Google Maps and Apple Maps buttons on every job." },
      { icon: Camera, title: "Photos & notes from site", body: "Job photos and notes upload from the field and appear in the office in real time." },
      { icon: Wallet, title: "Earnings visibility", body: "Techs see their own sales, commissions, and settlement history — no more end-of-month surprises." },
    ],
    bullets: [
      "iOS and Android apps plus mobile web portal",
      "Job status updates: accept, start, complete",
      "On-site estimates and invoices with your item library",
      "Card payment collection in the field",
      "Masked customer calling to protect privacy",
      "Permission controls decide what each tech can see",
    ],
    faqs: [
      { q: "How do technicians get access?", a: "Send an SMS invite from the Technicians page. The tech taps the setup link, creates a login, and immediately sees their assigned jobs." },
      { q: "Can I control what technicians see?", a: "Yes — granular permissions govern customer phone visibility, pricing, financials, status changes, and more, per technician or account-wide." },
      { q: "Can techs take payments?", a: "Yes — technicians collect card payments through the portal with Stripe. Funds go to your business account, and the invoice updates instantly." },
    ],
    related: ["dispatch", "call-masking", "payments"],
  },
  {
    slug: "automations",
    name: "Automations",
    icon: Zap,
    seoTitle: "Field Service Automation Software | Fieldz AI",
    seoDescription: "Put reminders, follow-ups, and review requests on autopilot. Trigger SMS and email automatically on job bookings, status changes, and completions.",
    h1: "Follow-ups that happen without you lifting a finger",
    intro: "Appointment reminders, en-route notices, review requests, and win-back campaigns run themselves — triggered by real job events, not manual to-do lists.",
    img: "/marketing/fieldz-ai-automations.webp",
    imgAlt: "Fieldz AI automations for reminders and review requests",
    benefits: [
      { icon: Zap, title: "Event-driven triggers", body: "Fire an SMS or email when a job is booked, assigned, started, completed, or paid — you define the rules once." },
      { icon: Star, title: "Reviews on autopilot", body: "Automatic review requests after completed jobs turn happy customers into public proof." },
      { icon: Bell, title: "Fewer no-shows", body: "Day-before and hour-before reminders reach customers automatically, cutting missed appointments." },
      { icon: FileText, title: "Smart templates", body: "Messages auto-fill names, times, addresses, technician details, and even masked contact numbers." },
    ],
    bullets: [
      "SMS and email automations from one builder",
      "Separate customer-facing and technician-facing messages",
      "Per-status triggers across the whole job lifecycle",
      "Campaigns for bulk announcements and offers",
      "Masking-aware placeholders that never leak real numbers",
      "Full send history for every automation",
    ],
    faqs: [
      { q: "What can trigger an automation?", a: "Job lifecycle events — booked, assigned, en-route, started, completed, paid — plus reminder schedules relative to the appointment time." },
      { q: "Can I automate review requests?", a: "Yes. After a job completes, an automatic SMS or email asks the customer for a review with your link — one of the highest-ROI automations you can turn on." },
      { q: "Can technicians get automated messages too?", a: "Yes — automations can target technicians with assignment details and reminders, using masking-aware placeholders so customer numbers stay protected." },
    ],
    related: ["sms-messaging", "lead-capture", "job-scheduling"],
  },
  {
    slug: "lead-capture",
    name: "Lead Capture",
    icon: ClipboardList,
    seoTitle: "Lead Capture & Web Booking Forms for Field Service | Fieldz AI",
    seoDescription: "Embeddable lead forms that create jobs automatically, missed-call capture, and source tracking — so no field service lead goes cold.",
    h1: "Capture every lead — from your website, your phone, anywhere",
    intro: "Embeddable web forms create customers and jobs automatically, inbound calls log themselves, and source tracking shows which channels actually bring work.",
    img: "/marketing/fieldz-ai-automations.webp",
    imgAlt: "Fieldz AI lead capture and intake",
    benefits: [
      { icon: ClipboardList, title: "Forms that create jobs", body: "Put a booking form on your website — submissions become customers and jobs in Fieldz AI instantly, no copy-paste." },
      { icon: PhoneCall, title: "Calls become records", body: "Every inbound call to your business line is logged and matched, so phone leads never vanish into sticky notes." },
      { icon: TrendingUp, title: "Source attribution", body: "Tag every job with its lead source and see revenue per channel in your financial reports." },
      { icon: Globe, title: "Marketplace leads", body: "Connect lead marketplaces like Thumbtack so incoming leads sync straight into your pipeline." },
    ],
    bullets: [
      "Shareable and embeddable public lead forms",
      "Automatic customer creation with duplicate matching",
      "Missed-call visibility on your dashboard",
      "Lead source tracking through to profit reporting",
      "Thumbtack integration for marketplace leads",
      "Instant notifications when new leads arrive",
    ],
    faqs: [
      { q: "How do web forms work?", a: "Create a form in Fieldz AI, share its link or embed it on your site. Each submission creates the customer and a job automatically, with a notification to your team." },
      { q: "Can I track where jobs come from?", a: "Yes — every job carries a lead source, and job-source reports show revenue and profit per channel so you can double down on what works." },
      { q: "Does it integrate with lead marketplaces?", a: "Yes — connect Thumbtack to sync leads and messages directly into Fieldz AI, so marketplace inquiries get the same fast follow-up as direct ones." },
    ],
    related: ["automations", "crm", "financial-reporting"],
  },
  {
    slug: "customer-portal",
    name: "Customer Portal",
    icon: LayoutDashboard,
    seoTitle: "Customer Portal for Field Service Businesses | Fieldz AI",
    seoDescription: "Give customers a self-serve portal: view jobs and invoices, pay online, manage addresses — fewer status calls, faster payments.",
    h1: "A self-serve portal your customers will actually use",
    intro: "Customers log in to see their jobs, invoices, and payment history, pay outstanding balances online, and keep their addresses up to date — without calling your office.",
    iconPanel: [
      { icon: LayoutDashboard, label: "Jobs & history" },
      { icon: Receipt, label: "Invoices & receipts" },
      { icon: CreditCard, label: "Online payments" },
      { icon: MapPin, label: "Address management" },
    ],
    imgAlt: "Fieldz AI customer portal",
    benefits: [
      { icon: LayoutDashboard, title: "Fewer status calls", body: "Customers check job status, appointment times, and history themselves instead of calling your office." },
      { icon: CreditCard, title: "Faster payments", body: "Outstanding invoices are one click from paid — customers pay online whenever it suits them." },
      { icon: Receipt, title: "Every document in one place", body: "Invoices, receipts, and payment history stay accessible long after the job is done." },
      { icon: Users, title: "Accurate customer data", body: "Customers keep their own contact details and service addresses current — your CRM stays clean." },
    ],
    bullets: [
      "Secure login tied to the customer's record",
      "Jobs list with statuses and appointment details",
      "Invoice viewing and online payment",
      "Guest pay pages for one-off payments without an account",
      "Multiple service address management",
      "Works on any device — no app install required",
    ],
    faqs: [
      { q: "How do customers get portal access?", a: "Invite them from their customer profile, or they can register with the email or phone number on file. Guest payment links work even without an account." },
      { q: "Can customers pay through the portal?", a: "Yes — open invoices show a pay button backed by Stripe. Payments record automatically and receipts send instantly." },
      { q: "Is the portal branded?", a: "The portal presents your business name and documents carry your branding, keeping the experience consistent with your invoices and estimates." },
    ],
    related: ["invoicing", "payments", "crm"],
  },
  {
    slug: "inventory-management",
    name: "Inventory",
    icon: Package,
    seoTitle: "Inventory Management for Field Service | Fieldz AI",
    seoDescription: "Track parts and stock across your team: low-stock alerts, technician stock visibility, and parts costs that flow into job profitability.",
    h1: "Track parts and stock without a separate system",
    intro: "Keep an eye on the parts your business runs on — stock levels, low-stock alerts, technician stock, and parts costs that flow straight into job profitability.",
    iconPanel: [
      { icon: Package, label: "Stock levels" },
      { icon: Bell, label: "Low-stock alerts" },
      { icon: Smartphone, label: "Tech stock view" },
      { icon: BarChart3, label: "Parts cost reporting" },
    ],
    imgAlt: "Fieldz AI inventory management",
    benefits: [
      { icon: Package, title: "One inventory of record", body: "Track items, quantities, and categories in the same system that runs your jobs — no spreadsheet drift." },
      { icon: Bell, title: "Low-stock warnings", body: "Dashboard alerts flag items running low before they stall a job." },
      { icon: Smartphone, title: "Techs see their stock", body: "Technicians check available stock from the mobile portal before heading to a job." },
      { icon: BarChart3, title: "Parts hit your P&L", body: "Parts costs recorded on jobs flow into job profitability and your operating results automatically." },
    ],
    bullets: [
      "Item catalog with quantities and categories",
      "Low-stock thresholds with dashboard alerts",
      "Technician stock visibility in the mobile portal",
      "Parts costs linked to job profitability",
      "Deep links from dashboard alerts to low items",
      "Search and filter across your whole catalog",
    ],
    faqs: [
      { q: "Do parts costs affect job profit?", a: "Yes — parts recorded on a job are subtracted in job costing, so your net profit and operating results reflect true costs." },
      { q: "Will I know when stock runs low?", a: "Yes. Set thresholds per item and the dashboard flags low-stock items with a one-click deep link to review them." },
      { q: "Can technicians see inventory?", a: "Yes — techs can check stock from their portal so they know what's available before promising a same-day fix." },
    ],
    related: ["financial-reporting", "technician-app", "job-scheduling"],
  },
];

export const getFeature = (slug) => FEATURES.find((f) => f.slug === slug);
